/* Work-item taxonomy (docs/ux/UX_CONTRACT.md §4b) — every task_type maps to
 * one of six resolution patterns. APPROVE = a proposal exists (incl.
 * incomplete); CONNECT = the record's SHAPE is incomplete (no confirmed
 * chain head, unfinished look-through) — the officer completes the chain;
 * TAKE OVER = the machine failed and a human takes the work over (stalled
 * document, write that did not land). Those two were one bucket until the
 * 2026-07-28 taxonomy ruling — four unrelated officer situations shared a
 * chip. No task type may be left unmapped: unknown types fall back to
 * "take over" so nothing ever dead-ends silently. */

export type Pattern = 'approve' | 'reconcile' | 'chase' | 'connect' | 'take over' | 'fyi'

const PATTERN_BY_TASK_TYPE: Record<string, Pattern> = {
  source_question: 'chase',
  proposal_review: 'approve',
  verification_signoff: 'approve',
  look_through_review: 'approve',
  evidence_approval: 'approve',
  trade_duplicate_question: 'reconcile',
  position_trade_missing: 'reconcile',
  position_balance_stale: 'reconcile',
  position_balance_rollforward: 'reconcile',
  position_leg_identity_violation: 'reconcile',
  instrument_trade_missing: 'reconcile',
  instrument_balance_stale: 'reconcile',
  commitment_supply_mismatch: 'reconcile',
  investigation_action: 'chase',
  request_overdue: 'chase',
  request_unlocked_copy: 'chase',
  verification_remediation: 'chase',
  document_stuck: 'take over',
  dead_letter_review: 'take over',
  look_through_incomplete: 'connect',
  daily_digest: 'fyi',
  // v1/v2 map drift closed (arch audit 2026-07 §3): explicit entries for
  // the CHECK-set types that previously rode the unknown-type fallback —
  // same 'take over' pattern, so Home styling and blocksRecord are
  // unchanged. chain_orphan/look_through_incomplete auto-resolve via the
  // chain_check listener; the backend answers their generic verbs with an
  // honest explanation. evidence_aged exists for legacy rows only (TAX-2).
  chain_orphan: 'connect',
}

export function patternFor(taskType: string): Pattern {
  return PATTERN_BY_TASK_TYPE[taskType] ?? 'take over'
}

/* The sign-off IS the verified state being claimed, not something standing in
 * its way. It is minted priority='high' (it should sit at the top of the
 * queue), so the generic rule below counted it as one more thing blocking the
 * record — the ring reads 100% green and the headline count goes UP by one at
 * the same instant. Demo dry run 2026-07-25, risk R4. */
const NEVER_BLOCKS = new Set(['verification_signoff'])

/** Blocks-the-record urgency: conflicts, takeovers, unconnected chains and
 * recon breaks stop the record from being trustworthy; everything else is
 * routine until late. ``connect`` inherits the blocking weight the
 * look-through/orphan types carried inside ``take over`` before the split. */
export function blocksRecord(taskType: string, priority: string): boolean {
  if (NEVER_BLOCKS.has(taskType)) return false
  const p = patternFor(taskType)
  return (
    p === 'reconcile' ||
    p === 'take over' ||
    p === 'connect' ||
    priority === 'urgent' ||
    priority === 'high'
  )
}
